<script setup lang="ts">
import { ref, watch } from 'vue'
import { api, extractError } from '@/api/client'
import AppInstructionItem from '@/components/apps/AppInstructionItem.vue'
import type { VpnAppItem } from '@/types'
import { formatOsList, resolveAppIconUrl } from '@/utils/apps'
import { safeUrl } from '@/utils/safeUrl'

const PROTOCOL_LABELS: Record<string, string> = {
  vless: 'VLESS',
  trojan: 'Trojan',
  ss: 'Shadowsocks',
  shadowsocks_2022: 'Shadowsocks 2022',
  hysteria2: 'Hysteria2',
  anytls: 'AnyTLS',
  tuic: 'TUIC',
  ssh: 'SSH',
}

const TRANSPORT_LABELS: Record<string, string> = {
  tcp: 'TCP',
  xhttp: 'xHTTP',
  udp: 'UDP',
  ws: 'WS',
  grpc: 'gRPC',
  h2: 'H2',
}

const SECURITY_LABELS: Record<string, string> = {
  reality: 'Reality',
  tls: 'TLS',
}

const os = ref('')
const protocol = ref('')
const transport = ref('')
const security = ref('')
const subscriptions = ref(false)

const apps = ref<VpnAppItem[]>([])
const loading = ref(false)
const error = ref('')
const brokenIcons = ref<Set<number>>(new Set())

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams()
    if (os.value) params.set('os', os.value)
    if (protocol.value) params.set('protocol', protocol.value)
    if (transport.value) params.set('transport', transport.value)
    if (security.value) params.set('security', security.value)
    if (subscriptions.value) params.set('subscriptions', '1')
    const qs = params.toString()
    const { data } = await api.get<{ success: boolean; apps: VpnAppItem[] }>(
      `/api/user/apps${qs ? `?${qs}` : ''}`,
    )
    apps.value = data.success ? data.apps : []
  } catch (e) {
    error.value = extractError(e, 'Ошибка загрузки приложений')
    apps.value = []
  } finally {
    loading.value = false
  }
}

watch([os, protocol, transport, security, subscriptions], () => void load(), { immediate: true })

function iconSrc(app: VpnAppItem): string | null {
  if (brokenIcons.value.has(app.id)) return null
  return resolveAppIconUrl(app.icon_url)
}

function onIconError(appId: number) {
  brokenIcons.value = new Set([...brokenIcons.value, appId])
}

function downloadHref(url: string): string {
  return safeUrl(url)
}

function labelList(items: string[] | undefined, labels: Record<string, string>): string {
  if (!items?.length) return ''
  return items.map((item) => labels[item] || item).join(', ')
}
</script>

<template>
  <div>
    <h1 class="page-title mb-6">
      <i class="bi bi-download mr-2" />Приложения для подключения
    </h1>

    <div class="panel mb-4">
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <div>
          <label for="app-os-filter" class="mb-1 block text-sm font-semibold">Операционная система</label>
          <select id="app-os-filter" v-model="os" class="form-select">
            <option value="">Все ОС</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
            <option value="linux">Linux</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
        </div>
        <div>
          <label for="app-proto-filter" class="mb-1 block text-sm font-semibold">Протокол</label>
          <select id="app-proto-filter" v-model="protocol" class="form-select">
            <option value="">Все протоколы</option>
            <option value="vless">VLESS</option>
            <option value="trojan">Trojan</option>
            <option value="ss">Shadowsocks</option>
            <option value="shadowsocks_2022">Shadowsocks 2022</option>
            <option value="hysteria2">Hysteria2</option>
            <option value="anytls">AnyTLS</option>
            <option value="tuic">TUIC</option>
            <option value="ssh">SSH</option>
          </select>
        </div>
        <div>
          <label for="app-transport-filter" class="mb-1 block text-sm font-semibold">Транспорт</label>
          <select id="app-transport-filter" v-model="transport" class="form-select">
            <option value="">Все транспорты</option>
            <option value="tcp">TCP</option>
            <option value="xhttp">xHTTP</option>
            <option value="udp">UDP</option>
            <option value="ws">WS</option>
            <option value="grpc">gRPC</option>
            <option value="h2">H2</option>
          </select>
        </div>
        <div>
          <label for="app-security-filter" class="mb-1 block text-sm font-semibold">Безопасность</label>
          <select id="app-security-filter" v-model="security" class="form-select">
            <option value="">Вся безопасность</option>
            <option value="reality">Reality</option>
            <option value="tls">TLS</option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-sm font-semibold">&nbsp;</label>
          <label class="mt-2 flex items-center gap-2 text-sm font-semibold">
            <input v-model="subscriptions" type="checkbox" class="h-4 w-4 rounded border-white-light text-primary" />
            Поддержка подписок
          </label>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-4">{{ error }}</div>

    <div v-if="loading" class="panel flex flex-col items-center justify-center py-10 text-muted">
      <span class="animate-[spin_2s_linear_infinite] border-8 border-l-primary border-r-primary rounded-full w-10 h-10 inline-block mb-3" />
      <span>Загрузка приложений…</span>
    </div>

    <div v-else-if="apps.length === 0" class="panel py-10 text-center text-muted">
      Приложений не найдено
    </div>

    <div v-else class="space-y-4">
      <article v-for="app in apps" :key="app.id" class="panel !p-5">
        <img
          v-if="iconSrc(app)"
          :src="iconSrc(app)!"
          :alt="app.title"
          class="mb-3 max-h-16 max-w-16 object-contain"
          @error="onIconError(app.id)"
        />

        <h2 class="text-lg font-bold">{{ app.title }}</h2>
        <p v-if="app.description" class="mt-1 text-sm text-muted">{{ app.description }}</p>

        <div class="mt-3 space-y-1 text-sm text-muted">
          <div v-if="app.os_types?.length">
            <span class="font-semibold text-black/70">ОС:</span>
            {{ formatOsList(app.os_types) }}
          </div>
          <div v-if="app.protocol_types?.length">
            <span class="font-semibold text-black/70">Протоколы:</span>
            {{ labelList(app.protocol_types, PROTOCOL_LABELS) }}
          </div>
          <div v-if="app.transport_types?.length">
            <span class="font-semibold text-black/70">Транспорт:</span>
            {{ labelList(app.transport_types, TRANSPORT_LABELS) }}
          </div>
          <div v-if="app.security_types?.length">
            <span class="font-semibold text-black/70">Безопасность:</span>
            {{ labelList(app.security_types, SECURITY_LABELS) }}
          </div>
          <div class="flex flex-wrap gap-2 pt-1">
            <span
              v-if="app.supports_subscriptions"
              class="badge bg-success text-white"
            >
              Поддержка подписок
            </span>
          </div>
        </div>

        <div v-if="app.downloads?.length" class="mt-4">
          <div class="mb-2 text-sm font-bold">Скачать:</div>
          <div class="flex flex-wrap gap-2">
            <a
              v-for="dl in app.downloads"
              :key="dl.id"
              :href="downloadHref(dl.download_url)"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-sm btn-primary"
            >
              <i class="bi bi-download mr-1" />{{ dl.name }}
            </a>
          </div>
        </div>

        <div
          v-if="app.instructions?.length"
          class="mt-4 space-y-2"
          :id="`instructionsAccordion${app.id}`"
        >
          <AppInstructionItem
            v-for="inst in app.instructions"
            :key="inst.id"
            :instruction="inst"
            :app-id="app.id"
          />
        </div>
      </article>
    </div>
  </div>
</template>

<style scoped>
.badge.bg-success {
  background-color: #198754;
}
</style>
